import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, CheckCircle, XCircle } from 'lucide-react';

const GestionAccesos = () => {
  const [roles, setRoles] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [accesos, setAccesos] = useState({});
  const [originalAccesos, setOriginalAccesos] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const opcionesAcceso = ['ninguna', 'lectura', 'edicion'];

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
      const [rolesRes, menusRes, permisosRes] = await Promise.all([
        fetch(`${API_URL}/admin/roles`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/admin/menus`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/admin/permisos`, { headers: getAuthHeaders() })
      ]);

      const rolesData = await rolesRes.json();
      const menusData = await menusRes.json();
      const permisosData = await permisosRes.json();

      setRoles(rolesData.roles || []);
      // Solo menús padre (módulos principales)
      setModulos((menusData.menus || []).filter(m => !m.id_padre));

      // Construir mapa de accesos: key = "id_rol-id_modulo"
      const accesosMap = {};
      (permisosData.permisos || []).forEach(p => {
        const key = `${p.id_rol}-${p.id_menu}`;
        accesosMap[key] = 'lectura'; // Default por ahora
      });

      setAccesos(accesosMap);
      setOriginalAccesos({ ...accesosMap });
    } catch (err) {
      console.error('Error fetching data:', err);
      showMessage('error', 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const getAcceso = (idRol, idModulo) => {
    const key = `${idRol}-${idModulo}`;
    return accesos[key] || 'ninguna';
  };

  const handleChangeAcceso = (idRol, idModulo, nuevoAcceso) => {
    const key = `${idRol}-${idModulo}`;
    setAccesos(prev => ({
      ...prev,
      [key]: nuevoAcceso
    }));
  };

  const hasChanges = () => {
    return JSON.stringify(accesos) !== JSON.stringify(originalAccesos);
  };

  const handleGuardar = async () => {
    if (!hasChanges()) {
      showMessage('success', 'No hay cambios para guardar');
      return;
    }

    setSaving(true);
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
    try {
      const nuevosPermisos = [];
      const permisosAEliminar = [];

      Object.entries(accesos).forEach(([key, tipoAcceso]) => {
        const accesoOriginal = originalAccesos[key];

        if (tipoAcceso !== 'ninguna' && (!accesoOriginal || accesoOriginal === 'ninguna')) {
          nuevosPermisos.push(key);
        } else if (tipoAcceso === 'ninguna' && accesoOriginal && accesoOriginal !== 'ninguna') {
          permisosAEliminar.push(key);
        }
      });

      // Crear permisos
      if (nuevosPermisos.length > 0) {
        for (const p of nuevosPermisos) {
          const [id_rol, id_menu] = p.split('-');
          try {
            await fetch(`${API_URL}/admin/permisos`, {
              method: 'POST',
              headers: getAuthHeaders(),
              body: JSON.stringify({ id_rol: parseInt(id_rol), id_menu: parseInt(id_menu) })
            });
          } catch (error) {
            console.error('Error creating permission:', error);
          }
        }
      }

      // Eliminar permisos
      if (permisosAEliminar.length > 0) {
        for (const p of permisosAEliminar) {
          const [id_rol, id_menu] = p.split('-');
          try {
            await fetch(`${API_URL}/admin/permisos/${id_rol}/${id_menu}`, {
              method: 'DELETE',
              headers: getAuthHeaders()
            });
          } catch (error) {
            console.error('Error deleting permission:', error);
          }
        }
      }

      await fetchData();
      showMessage('success', 'Cambios guardados exitosamente');
    } catch (err) {
      console.error('Error saving:', err);
      showMessage('error', 'Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin h-8 w-8 text-[#1e3a8a]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">
          Administrador &gt; Gestión de Accesos
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Configura los permisos de acceso para cada rol a los diferentes módulos del sistema
        </p>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`p-4 rounded-lg flex items-center space-x-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <XCircle size={20} />}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      {/* Tabla de Accesos */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr className="border-b-2 border-gray-300">
                <th className="px-4 py-3 text-left font-semibold border sticky left-0 bg-gray-100 z-10">
                  Rol
                </th>
                {modulos.map(modulo => (
                  <th key={modulo.id_menu} className="px-4 py-3 text-center font-semibold border min-w-[150px]">
                    {modulo.etiqueta}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {roles.map(rol => (
                <tr key={rol.id_rol} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3 border sticky left-0 bg-white z-10">
                    <div>
                      <div className="font-semibold text-gray-900">{rol.nombre}</div>
                      <div className="text-xs text-gray-500">{rol.descripcion}</div>
                    </div>
                  </td>
                  {modulos.map(modulo => {
                    const accesoActual = getAcceso(rol.id_rol, modulo.id_menu);
                    return (
                      <td key={`${rol.id_rol}-${modulo.id_menu}`} className="px-4 py-3 text-center border">
                        <select
                          value={accesoActual}
                          onChange={(e) => handleChangeAcceso(rol.id_rol, modulo.id_menu, e.target.value)}
                          className={`px-3 py-1.5 rounded-full border text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-100 ${
                            accesoActual === 'ninguna'
                              ? 'bg-gray-100 text-gray-600 border-gray-300'
                              : accesoActual === 'lectura'
                              ? 'bg-blue-50 text-blue-700 border-blue-300'
                              : 'bg-green-50 text-green-700 border-green-300'
                          }`}
                        >
                          {opcionesAcceso.map(opcion => (
                            <option key={opcion} value={opcion}>
                              {opcion.charAt(0).toUpperCase() + opcion.slice(1)}
                            </option>
                          ))}
                        </select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {roles.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No hay roles configurados
          </div>
        )}
      </div>

      {/* Botón Guardar */}
      <div className="flex justify-end">
        <button
          onClick={handleGuardar}
          disabled={!hasChanges() || saving}
          className={`px-6 py-2 rounded-full font-medium flex items-center space-x-2 text-sm transition ${
            hasChanges() && !saving
              ? 'bg-[#1e3a8a] text-white hover:bg-[#2c4a9a]'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {saving ? (
            <>
              <RefreshCw className="animate-spin" size={18} />
              <span>Guardando...</span>
            </>
          ) : (
            <>
              <Save size={18} />
              <span>Guardar Cambios</span>
            </>
          )}
        </button>
      </div>

      {/* Leyenda */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
        <h3 className="font-semibold text-gray-800 mb-3 text-sm">Leyenda de Accesos</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <div className="px-3 py-1 bg-gray-100 text-gray-600 border border-gray-300 rounded-full text-xs font-medium">
              Ninguna
            </div>
            <span className="text-xs text-gray-600">Sin acceso al módulo</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="px-3 py-1 bg-blue-50 text-blue-700 border border-blue-300 rounded-full text-xs font-medium">
              Lectura
            </div>
            <span className="text-xs text-gray-600">Solo puede visualizar</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="px-3 py-1 bg-green-50 text-green-700 border border-green-300 rounded-full text-xs font-medium">
              Edición
            </div>
            <span className="text-xs text-gray-600">Puede modificar datos</span>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-xs text-blue-800">
          <strong>Nota:</strong> Los cambios se guardan al hacer clic en "Guardar Cambios". Los usuarios verán los cambios reflejados en su próximo inicio de sesión.
        </p>
      </div>
    </div>
  );
};

export default GestionAccesos;